import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Image,
  Tag,
  Spin,
  message,
  Tabs,
  Row,
  Col,
  Typography,
  Space,
  Empty,
  Divider,
  Dropdown,
  Menu,
  Modal,
  Form,
  Input,
  Select,
  Radio,
  Popconfirm,
  Tooltip,
} from 'antd';
import { useToast } from '../components/Toast';
import {
  ArrowLeftOutlined,
  EditOutlined,
  SaveOutlined,
  PlusOutlined,
  MoreOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PictureOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { lifetreeApi } from '../api/lifetreeApi';
import {
  LifetreeDetailResponse,
  NodeItem,
  CreateNodeRequest,
  UpdateLifetreeRequest,
  StructureUpdateRequest,
  CreateCharacterRequest,
  NextNodeSuggestion,
  NodeDetailResponse,
} from '../api/models/Lifetree';
import AddNodeModal from '../components/AddNodeModal';
import CreateCharacterModal from '../components/CreateCharacterModal';
import { PageTitleSetter } from '../components/PageTitleContext';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

// Helper function to convert number to Roman numerals
const toRoman = (num: number): string => {
  const romanNumerals = [
    { value: 1000, symbol: 'M' },
    { value: 900, symbol: 'CM' },
    { value: 500, symbol: 'D' },
    { value: 400, symbol: 'CD' },
    { value: 100, symbol: 'C' },
    { value: 90, symbol: 'XC' },
    { value: 50, symbol: 'L' },
    { value: 40, symbol: 'XL' },
    { value: 10, symbol: 'X' },
    { value: 9, symbol: 'IX' },
    { value: 5, symbol: 'V' },
    { value: 4, symbol: 'IV' },
    { value: 1, symbol: 'I' }
  ];

  let result = '';
  for (const { value, symbol } of romanNumerals) {
    while (num >= value) {
      result += symbol;
      num -= value;
    }
  }
  return result;
};

interface NodeWithPosition extends NodeItem {
  x?: number;
  y?: number;
}

interface Connection {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  fromPoint: 'output';
  toPoint: 'input';
}

interface DragConnection {
  startNodeId: string;
  startPoint: 'input' | 'output';
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

// Connection Point Component
const ConnectionPoint: React.FC<{
  nodeId: string;
  type: 'input' | 'output';
  onConnectionStart: (nodeId: string, type: 'input' | 'output', event: React.MouseEvent) => void;
  onDeleteConnections?: (nodeId: string) => void;
  hasConnection?: boolean;
  connectionCount?: number;
}> = ({ nodeId, type, onConnectionStart, onDeleteConnections, hasConnection = false, connectionCount = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Connection point clicked:', nodeId, type);
    
    // If it's an input point with connections, delete them on right click
    if (type === 'input' && hasConnection && e.button === 2 && onDeleteConnections) {
      onDeleteConnections(nodeId);
      return;
    }
    
    // If it's an input point with connections, delete them on double click
    if (type === 'input' && hasConnection && e.detail === 2 && onDeleteConnections) {
      onDeleteConnections(nodeId);
      return;
    }
    
    onConnectionStart(nodeId, type, e);
  };

  return (
    <div
      data-connection-point="true"
      data-node-id={nodeId}
      data-connection-type={type}
      style={{
        position: 'absolute',
        width: isHovered ? 18 : 14,
        height: isHovered ? 18 : 14,
        borderRadius: '50%',
        backgroundColor: hasConnection ? '#52c41a' : (isHovered ? '#40a9ff' : '#1890ff'),
        border: '3px solid white',
        cursor: 'crosshair',
        zIndex: 30,
        boxShadow: isHovered ? '0 4px 8px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.2)',
        transition: 'all 0.2s ease',
        ...(type === 'input' 
          ? { left: isHovered ? -9 : -7, top: '50%', transform: 'translateY(-50%)' }
          : { right: isHovered ? -9 : -7, top: '50%', transform: 'translateY(-50%)' }
        )
      }}
      onMouseDown={handleMouseDown}
      onContextMenu={(e) => {
        if (type === 'input' && hasConnection && onDeleteConnections) {
          e.preventDefault();
          onDeleteConnections(nodeId);
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={type === 'input' && hasConnection ? 
        `Input connection: ${connectionCount}/1 (Right-click to delete)` : 
        type === 'input' ? `Input connection: ${connectionCount}/1` : 
        `Output connections: ${connectionCount}`}
    >
      {/* Show connection count for outputs, or warning for inputs with connections */}
      {((type === 'output' && connectionCount > 1) || (type === 'input' && connectionCount > 0)) && (
        <div
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: type === 'input' && connectionCount > 0 ? '#52c41a' : '#ff4d4f',
            color: 'white',
            fontSize: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            zIndex: 1,
          }}
        >
          {type === 'input' ? '1' : connectionCount}
        </div>
      )}
    </div>
  );
};

// Draggable Node Card Component
const DraggableNodeCard: React.FC<{ 
  node: NodeItem; 
  onConnectionStart: (nodeId: string, type: 'input' | 'output', event: React.MouseEvent) => void;
  onDeleteConnections: (nodeId: string) => void;
  connections: Connection[];
  isValidTarget?: boolean;
  onNodeDetail?: (nodeId: string) => void;
  onSuggestNextNodes?: (node: NodeItem) => void;
  suggestingNextNodes?: boolean;
  parentNodeForSuggestions?: string | null;
}> = ({ node, onConnectionStart, onDeleteConnections, connections, isValidTarget = false, onNodeDetail, onSuggestNextNodes, suggestingNextNodes, parentNodeForSuggestions }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: node.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Check if node has input/output connections
  const hasInputConnection = connections.some(conn => conn.toNodeId === node.id);
  const hasOutputConnection = connections.some(conn => conn.fromNodeId === node.id);
  
  // Count connections for debugging
  const inputConnectionCount = connections.filter(conn => conn.toNodeId === node.id).length;
  const outputConnectionCount = connections.filter(conn => conn.fromNodeId === node.id).length;

  // Menu items for node actions
  const menuItems = [
    {
      key: 'detail',
      icon: <EyeOutlined />,
      label: 'Detail',
      onClick: () => onNodeDetail && onNodeDetail(node.id),
    },
    {
      key: 'ai-suggest',
      icon: <RobotOutlined />,
      label: 'Gợi ý Node tiếp theo',
      onClick: () => onSuggestNextNodes && onSuggestNextNodes(node),
    }
  ];

  const handleMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div style={{ position: 'relative', width: 120, margin: '8px' }}>
      {/* Connection Points - Outside of draggable area */}
      <ConnectionPoint
        nodeId={node.id}
        type="input"
        onConnectionStart={onConnectionStart}
        onDeleteConnections={onDeleteConnections}
        hasConnection={hasInputConnection}
        connectionCount={inputConnectionCount}
      />
      
      <ConnectionPoint
        nodeId={node.id}
        type="output"
        onConnectionStart={onConnectionStart}
        hasConnection={hasOutputConnection}
        connectionCount={outputConnectionCount}
      />

      {/* Draggable Node Card */}
      <div 
        ref={setNodeRef} 
        data-node-id={node.id}
        style={{ 
          ...style, 
          position: 'relative',
          width: '100%',
          height: 100,
          borderRadius: '8px',
          overflow: 'hidden',
          border: isValidTarget ? '3px solid #52c41a' : '2px solid #d9d9d9',
          cursor: 'move',
          boxShadow: isValidTarget ? '0 0 12px rgba(82, 196, 26, 0.6)' : '0 2px 4px rgba(0,0,0,0.1)',
          backgroundImage: node.imageUrl ? `url(${node.imageUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'top center',
          backgroundColor: node.imageUrl ? 'transparent' : '#f0f0f0',
        }}
        {...attributes} 
        {...listeners}
        className={`node-card ${isDragging ? 'dragging' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Level tag - Top left corner */}
        {node.level !== null && node.level > 0 && (
          <Tag 
            style={{ 
              position: 'absolute',
              top: 4,
              left: 4,
              fontSize: '10px',
              padding: '2px 6px',
              lineHeight: '1.2',
              height: 'auto',
              margin: 0,
              backgroundColor: '#1890ff',
              color: 'white',
              border: 'none',
              fontWeight: 'bold',
              zIndex: 2,
            }}
          >
            {toRoman(node.level)}
          </Tag>
        )}

        {/* Menu button - Top right corner */}
        <div
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 2,
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease',
            display: 'flex',
            gap: '4px',
            pointerEvents: isHovered ? 'auto' : 'none',
          }}
          onMouseDown={handleMenuClick}
          onClick={handleMenuClick}
        >
          <Button
            type="text"
            size="small"
            icon={<RobotOutlined />}
            loading={suggestingNextNodes && parentNodeForSuggestions === node.id}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSuggestNextNodes && onSuggestNextNodes(node);
            }}
            style={{
              width: 20,
              height: 20,
              minWidth: 20,
              padding: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              color: '#1890ff',
              border: 'none',
              boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
            }}
            title="Gợi ý Node tiếp theo"
          />
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              style={{
                width: 20,
                height: 20,
                minWidth: 20,
                padding: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: '#666',
                border: 'none',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </Dropdown>
        </div>

        {/* Title overlay at bottom */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
            padding: '8px 6px 4px 6px',
            textAlign: 'center',
          }}
        >
          <Text 
            style={{ 
              fontSize: '10px', 
              color: 'white',
              lineHeight: '1.2',
              display: 'block',
              wordBreak: 'break-word',
              fontWeight: 500,
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
            }}
            title={node.title}
          >
            {node.title.length > 18 ? `${node.title.substring(0, 18)}...` : node.title}
          </Text>
        </div>

        {/* No image placeholder */}
        {!node.imageUrl && (
          <div style={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#999',
            fontSize: '10px',
            pointerEvents: 'none',
          }}>
            No Image
          </div>
        )}
      </div>
    </div>
  );
};

// Droppable Level Column Component
const DroppableColumn: React.FC<{
  level: number | 'unassigned';
  nodes: NodeItem[];
  title: string;
  onConnectionStart: (nodeId: string, type: 'input' | 'output', event: React.MouseEvent) => void;
  onDeleteConnections: (nodeId: string) => void;
  connections: Connection[];
  validTargetNodes: string[];
  onNodeDetail?: (nodeId: string) => void;
  onSuggestNextNodes?: (node: NodeItem) => void;
  suggestingNextNodes?: boolean;
  parentNodeForSuggestions?: string | null;
}> = ({ level, nodes, title, onConnectionStart, onDeleteConnections, connections, validTargetNodes, onNodeDetail, onSuggestNextNodes, suggestingNextNodes, parentNodeForSuggestions }) => {
  const columnId = level === 'unassigned' ? 'unassigned' : `level-${level}`;
  const { isOver, setNodeRef } = useDroppable({
    id: columnId,
  });
  
  return (
    <div 
      ref={setNodeRef}
      style={{ 
        minWidth: 160, 
        marginRight: 12,
        border: `2px dashed ${isOver ? '#1890ff' : '#d9d9d9'}`,
        borderRadius: '8px',
        padding: '12px',
        backgroundColor: isOver ? '#e6f7ff' : '#fafafa',
        minHeight: 180,
        transition: 'all 0.2s ease',
        position: 'relative'
      }}
    >
      <Title level={5} style={{ textAlign: 'center', marginBottom: 12, fontSize: '14px' }}>
        {title}
      </Title>
      <SortableContext items={nodes.map(n => n.id)} strategy={verticalListSortingStrategy}>
        <div style={{ minHeight: 180 }}>
          {nodes.map(node => (
            <DraggableNodeCard 
              key={node.id} 
              node={node} 
              onConnectionStart={onConnectionStart}
              onDeleteConnections={onDeleteConnections}
              connections={connections}
              isValidTarget={validTargetNodes.includes(node.id)}
              onNodeDetail={onNodeDetail}
              onSuggestNextNodes={onSuggestNextNodes}
              suggestingNextNodes={suggestingNextNodes}
              parentNodeForSuggestions={parentNodeForSuggestions}
            />
          ))}
          {nodes.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              color: '#999', 
              padding: '30px 0',
              fontSize: '11px'
            }}>
              {level === 'unassigned' ? 'No unassigned nodes' : 'Drop nodes here'}
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

// Connection Line Component  
const ConnectionLine: React.FC<{ 
  connection: Connection; 
  fromElement: HTMLElement | null;
  toElement: HTMLElement | null;
}> = ({ connection, fromElement, toElement }) => {
  console.log('ConnectionLine render:', connection.id, fromElement, toElement);
  
  if (!fromElement || !toElement) {
    console.log('Missing elements for connection:', connection.id);
    return null;
  }
  
  const fromRect = fromElement.getBoundingClientRect();
  const toRect = toElement.getBoundingClientRect();
  const containerRect = fromElement.closest('.tree-container')?.getBoundingClientRect();
  
  console.log('Rects:', { fromRect, toRect, containerRect });
  
  if (!containerRect) {
    console.log('No container rect found');
    return null;
  }
  
  // Calculate connection points (center of connection point circles)
  const fromX = fromRect.left + fromRect.width / 2 - containerRect.left;
  const fromY = fromRect.top + fromRect.height / 2 - containerRect.top;
  const toX = toRect.left + toRect.width / 2 - containerRect.left;
  const toY = toRect.top + toRect.height / 2 - containerRect.top;
  
  // Create curved path
  const midX = (fromX + toX) / 2;
  const path = `M ${fromX} ${fromY} Q ${midX} ${fromY} ${midX} ${(fromY + toY) / 2} Q ${midX} ${toY} ${toX} ${toY}`;
  
  return (
    <svg 
      style={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%', 
        pointerEvents: 'none', 
        zIndex: 1 
      }}
    >
      <defs>
        <marker 
          id={`arrowhead-${connection.id}`} 
          markerWidth="8" 
          markerHeight="6" 
          refX="7" 
          refY="3" 
          orient="auto"
        >
          <polygon points="0 0, 8 3, 0 6" fill="#1890ff" />
        </marker>
      </defs>
      <path
        d={path}
        stroke="#1890ff"
        strokeWidth="2"
        fill="none"
        markerEnd={`url(#arrowhead-${connection.id})`}
      />
    </svg>
  );
};

const LifetreeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [lifetree, setLifetree] = useState<LifetreeDetailResponse | null>(null);
  const [treeStructure, setTreeStructure] = useState<{ [level: number]: NodeWithPosition[] }>({});
  const [unassignedNodes, setUnassignedNodes] = useState<NodeWithPosition[]>([]);
  const [addNodeModalVisible, setAddNodeModalVisible] = useState(false);
  const [addNodeLoading, setAddNodeLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [activeNode, setActiveNode] = useState<NodeItem | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [addCharacterModalVisible, setAddCharacterModalVisible] = useState(false);
  const [addCharacterLoading, setAddCharacterLoading] = useState(false);
  
  // Edit Lifetree Modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm] = Form.useForm();
  const [activatingLifetree, setActivatingLifetree] = useState(false);

  // AI Generate Nodes state
  const [generatingNodes, setGeneratingNodes] = useState(false);
  
  // AI Suggest Outline state
  const [suggestingOutline, setSuggestingOutline] = useState(false);
  const [refiningOutline, setRefiningOutline] = useState(false);
  const [outlineFeedback, setOutlineFeedback] = useState('');
  const [outlineModalVisible, setOutlineModalVisible] = useState(false);
  const [suggestedOutline, setSuggestedOutline] = useState('');
  
  // AI Suggest Next Nodes state
  const [suggestingNextNodes, setSuggestingNextNodes] = useState(false);
  const [nodeSuggestionsModalVisible, setNodeSuggestionsModalVisible] = useState(false);
  const [nodeSuggestions, setNodeSuggestions] = useState<NextNodeSuggestion[]>([]);
  const [parentNodeForSuggestions, setParentNodeForSuggestions] = useState<NodeItem | null>(null);
  const [applyingSuggestion, setApplyingSuggestion] = useState(false);

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || '1');

  // Cover image picker state
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [coverPickerImages, setCoverPickerImages] = useState<{ url: string; label: string; stateName?: string; avatar?: string | null; width?: number; height?: number; nodeTitle: string }[]>([]);
  const [coverPickerLoading, setCoverPickerLoading] = useState(false);
  const [coverSelectedUrl, setCoverSelectedUrl] = useState<string | null>(null);
  const [settingCover, setSettingCover] = useState(false);

  // Lifetree Image Management state
  const [imageManagerOpen, setImageManagerOpen] = useState(false);
  const [lifetreeImages, setLifetreeImages] = useState<any[]>([]);
  const [fetchingLifetreeImages, setFetchingLifetreeImages] = useState(false);
  const [representativeNodeId, setRepresentativeNodeId] = useState<string | undefined>(undefined);
  const [isGeneratingConcept, setIsGeneratingConcept] = useState(false);
  const [aiConcept, setAiConcept] = useState<any | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  const fetchLifetreeImages = async () => {
    if (!id) return;
    setFetchingLifetreeImages(true);
    try {
      const images = await lifetreeApi.getLifetreeImages(id);
      setLifetreeImages(images);
    } catch (error: any) {
      toast.error('Lỗi', 'Không thể lấy danh sách ảnh Lifetree');
    } finally {
      setFetchingLifetreeImages(false);
    }
  };

  const handleGenerateConcept = async () => {
    if (!id || !representativeNodeId) {
      message.warning('Vui lòng chọn Node đại diện');
      return;
    }
    setIsGeneratingConcept(true);
    try {
      const result = await lifetreeApi.createLifetreeImagePrompt(id, representativeNodeId);
      setAiConcept(result);
    } catch (error: any) {
      toast.error('Lỗi', 'Không thể tạo đề xuất concept');
    } finally {
      setIsGeneratingConcept(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!id || !representativeNodeId || !aiConcept) return;
    setIsGeneratingImage(true);
    try {
      const description = aiConcept.technicalPrompt;
      const involvedCharacterIds = aiConcept.involvedCharacterIds || [];
      await lifetreeApi.createLifetreeImageWithPredefinedPrompt(id, representativeNodeId, { description, involvedCharacterIds });
      toast.success('Thành công', 'Đã hoàn tất sinh ảnh Lifetree');
      await fetchLifetreeImages();
      // Switch to list tab if needed or just stay
    } catch (error: any) {
      toast.error('Lỗi', 'Sinh ảnh thất bại');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleSelectLifetreeCover = async (imageUrl: string) => {
    if (!id || !lifetree) return;
    try {
      const updateData: UpdateLifetreeRequest = {
        title: lifetree.title,
        description: lifetree.description,
        imageUrl,
        tags: lifetree.tags,
        language: lifetree.language,
        outline: lifetree.outline,
        status: lifetree.status,
      };
      const updated = await lifetreeApi.updateLifetree(id, updateData);
      setLifetree(updated);
      toast.success('Thành công', 'Đã cập nhật ảnh bìa Lifetree');
    } catch (error: any) {
      toast.error('Lỗi', 'Không thể cập nhật ảnh bìa');
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key }, { replace: true });
  };
  
  // Connection states
  const [connections, setConnections] = useState<Connection[]>([]);
  const [dragConnection, setDragConnection] = useState<DragConnection | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [validTargetNodes, setValidTargetNodes] = useState<string[]>([]);
  const [originalNodes, setOriginalNodes] = useState<NodeItem[]>([]);
  const [originalConnections, setOriginalConnections] = useState<Connection[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    if (id) {
      fetchLifetreeDetail();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Force re-render of connection lines when tree structure changes
  useEffect(() => {
    if (connections.length > 0) {
      const timer = setTimeout(() => {
        console.log('Tree structure changed, forcing connection lines update...');
        setConnections(prev => [...prev]);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [treeStructure, unassignedNodes]);

  // Force re-render of connection lines when nodes change
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     // Trigger a re-render to update connection lines
  //     setConnections(prev => [...prev]);
  //   }, 100);
  //   return () => clearTimeout(timer);
  // }, [treeStructure, unassignedNodes]);

  const fetchLifetreeDetail = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await lifetreeApi.getLifetreeDetail(id);
      console.log('=== FETCHED LIFETREE DETAIL ===');
      console.log('Response:', response);
      
      setLifetree(response);
      
      // Organize nodes first
      organizeNodes(response.nodes);
      
      // Then initialize connections based on nextNodeIds
      initializeConnections(response.nodes);
      
      // Store original state for comparison
      setOriginalNodes([...response.nodes]);
      const originalConnectionList: Connection[] = [];
      response.nodes.forEach(node => {
        node.nextNodeIds.forEach(nextNodeId => {
          originalConnectionList.push({
            id: `${node.id}-${nextNodeId}`,
            fromNodeId: node.id,
            toNodeId: nextNodeId,
            fromPoint: 'output',
            toPoint: 'input'
          });
        });
      });
      setOriginalConnections(originalConnectionList);
      
      console.log('=== FETCH COMPLETE ===');
      console.log('Nodes:', response.nodes.length);
      console.log('Original connections:', originalConnectionList.length);
    } catch (error: any) {
      toast.error('Failed to fetch lifetree detail', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setLoading(false);
    }
  };

  const organizeNodes = (nodes: NodeItem[]) => {
    const structure: { [level: number]: NodeWithPosition[] } = {};
    const unassigned: NodeWithPosition[] = [];

    nodes.forEach(node => {
      if (node.level === null || node.level === 0) {
        unassigned.push(node);
      } else {
        if (!structure[node.level]) {
          structure[node.level] = [];
        }
        structure[node.level].push(node);
      }
    });

    setTreeStructure(structure);
    setUnassignedNodes(unassigned);
  };

  const initializeConnections = (nodes: NodeItem[]) => {
    console.log('=== INITIALIZING CONNECTIONS ===');
    console.log('Nodes received:', nodes.length);
    
    const connectionList: Connection[] = [];
    const nodeIds = new Set(nodes.map(n => n.id));
    
    nodes.forEach(node => {
      console.log(`Processing node ${node.title} (${node.id}) with nextNodeIds:`, node.nextNodeIds);
      
      node.nextNodeIds.forEach(nextNodeId => {
        // Validate that the target node exists
        if (!nodeIds.has(nextNodeId)) {
          console.warn(`Target node ${nextNodeId} not found for connection from ${node.id}`);
          return;
        }
        
        // Check for duplicate connections
        const existingConnection = connectionList.find(conn => 
          conn.fromNodeId === node.id && conn.toNodeId === nextNodeId
        );
        
        if (existingConnection) {
          console.warn(`Duplicate connection detected: ${node.id} -> ${nextNodeId}`);
          return;
        }
        
        const connection: Connection = {
          id: `${node.id}-${nextNodeId}`,
          fromNodeId: node.id,
          toNodeId: nextNodeId,
          fromPoint: 'output',
          toPoint: 'input'
        };
        
        connectionList.push(connection);
        console.log(`Created connection: ${node.title} -> ${nodes.find(n => n.id === nextNodeId)?.title || nextNodeId}`);
      });
    });
    
    console.log('Total connections created:', connectionList.length);
    console.log('Connections:', connectionList);
    setConnections(connectionList);
    
    // Force re-render of connection lines after a short delay
    setTimeout(() => {
      console.log('Forcing connection lines re-render...');
      setConnections(prev => [...prev]);
    }, 100);
  };

  const handleDragStart = (event: DragStartEvent) => {
    if (isConnecting) {
      console.log('Drag start prevented - in connection mode');
      return; // Disable drag when connecting
    }
    const { active } = event;
    const node = findNodeById(active.id as string);
    setActiveNode(node);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (isConnecting) return; // Disable drag when connecting
    const { active, over } = event;
    setActiveNode(null);

    if (!over) return;

    const activeNodeId = active.id as string;
    const overId = over.id as string;

    // Determine target level from drop zone
    let targetLevel: number | null = null;
    if (overId === 'unassigned') {
      targetLevel = null;
    } else if (overId.startsWith('level-')) {
      targetLevel = parseInt(overId.replace('level-', ''));
    } else {
      // Dropped on another node, find its level
      const targetNode = findNodeById(overId);
      if (targetNode) {
        targetLevel = targetNode.level;
      }
    }

    const activeNode = findNodeById(activeNodeId);
    if (!activeNode) return;

    // Case 1: Moving to a different level
    if (activeNode.level !== targetLevel) {
      updateNodeLevel(activeNodeId, targetLevel);
      setHasChanges(true);
      return;
    }

    // Case 2: Reordering within the same level
    if (activeNodeId !== overId) {
      const sourceLevel = activeNode.level;
      
      if (sourceLevel === null || sourceLevel === 0) {
        // Reordering in unassigned nodes
        const oldIndex = unassignedNodes.findIndex(node => node.id === activeNodeId);
        const newIndex = unassignedNodes.findIndex(node => node.id === overId);
        
        if (oldIndex !== -1 && newIndex !== -1) {
          setUnassignedNodes(prev => arrayMove(prev, oldIndex, newIndex));
          setHasChanges(true);
        }
      } else {
        // Reordering in a specific level
        const levelNodes = treeStructure[sourceLevel] || [];
        const oldIndex = levelNodes.findIndex(node => node.id === activeNodeId);
        const newIndex = levelNodes.findIndex(node => node.id === overId);
        
        if (oldIndex !== -1 && newIndex !== -1) {
          setTreeStructure(prev => ({
            ...prev,
            [sourceLevel]: arrayMove(levelNodes, oldIndex, newIndex)
          }));
          setHasChanges(true);
        }
      }
    }
  };

  const handleConnectionStart = (nodeId: string, type: 'input' | 'output', event: React.MouseEvent) => {
    setIsConnecting(true);
    
    // Calculate valid target nodes
    let validTargets: string[] = [];
    const sourceNode = findNodeById(nodeId);
    
    if (sourceNode && type === 'output') {
      // For output connections: find nodes that can be connected to
      validTargets = getAllNodes().filter((node: NodeItem) => {
        if (node.id === nodeId) return false; // Can't connect to self
        if (node.level === null || sourceNode.level === null) return false; // Skip null levels
        if (node.level !== sourceNode.level + 1) return false; // Target level must be source level + 1
        
        // Check if target node already has an input connection
        const hasInputConnection = connections.some(conn => conn.toNodeId === node.id);
        return !hasInputConnection; // Only show nodes without input connections
      }).map((node: NodeItem) => node.id);
      
      setValidTargetNodes(validTargets);
      console.log('Valid target nodes for output connection:', validTargets);
    } else if (sourceNode && type === 'input') {
      // For input connections: find nodes that can connect to this node
      // Only nodes from the previous level that don't already connect to this node
      validTargets = getAllNodes().filter((node: NodeItem) => {
        if (node.id === nodeId) return false; // Can't connect to self
        if (node.level === null || sourceNode.level === null) return false; // Skip null levels
        if (node.level !== sourceNode.level - 1) return false; // Source level must be target level - 1
        
        // Check if this connection already exists
        const connectionExists = connections.some(conn => 
          conn.fromNodeId === node.id && conn.toNodeId === nodeId
        );
        return !connectionExists; // Only show nodes that aren't already connected
      }).map((node: NodeItem) => node.id);
      
      setValidTargetNodes(validTargets);
      console.log('Valid target nodes for input connection:', validTargets);
    } else {
      setValidTargetNodes([]);
    }
    
    // Change cursor to indicate connection mode
    document.body.style.cursor = 'crosshair';
    
    // Get the connection point element position
    const connectionPoint = event.currentTarget as HTMLElement;
    const rect = connectionPoint.getBoundingClientRect();
    const startX = rect.left + rect.width / 2;
    const startY = rect.top + rect.height / 2;
    
    const initialDragConnection = {
      startNodeId: nodeId,
      startPoint: type,
      startX: startX,
      startY: startY,
      currentX: startX,
      currentY: startY
    };
    
    setDragConnection(initialDragConnection);

    // Store the drag connection data in a variable that won't be affected by state updates
    let currentDragConnection = initialDragConnection;
    let currentValidTargets = validTargets;
    
    // Update valid targets dynamically based on current connections
    const updateValidTargets = () => {
      if (currentDragConnection.startPoint === 'output' && sourceNode) {
        const currentConnections = connections;
        currentValidTargets = getAllNodes().filter((node: NodeItem) => {
          if (node.id === currentDragConnection.startNodeId) return false;
          if (node.level === null || sourceNode.level === null) return false;
          if (node.level !== sourceNode.level + 1) return false;
          
          const hasInputConnection = currentConnections.some(conn => conn.toNodeId === node.id);
          return !hasInputConnection;
        }).map((node: NodeItem) => node.id);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      currentDragConnection = {
        ...currentDragConnection,
        currentX: e.clientX,
        currentY: e.clientY
      };
      setDragConnection(currentDragConnection);
    };

    const handleMouseUp = (e: MouseEvent) => {
      console.log('Mouse up at:', e.clientX, e.clientY);
      console.log('Current drag connection:', currentDragConnection);
      
      if (!currentDragConnection) {
        console.log('No drag connection');
        cleanup();
        return;
      }
      
      // Find the node element under mouse (not just connection point)
      const elementUnder = document.elementFromPoint(e.clientX, e.clientY);
      console.log('Element under mouse:', elementUnder);
      
      // Look for any node element (with data-node-id)
      const nodeElement = elementUnder?.closest('[data-node-id]');
      console.log('Node element found:', nodeElement);
      
      if (nodeElement) {
        const targetNodeId = nodeElement.getAttribute('data-node-id');
        console.log('Target node ID:', targetNodeId);
        console.log('Start node ID:', currentDragConnection.startNodeId);
        console.log('Start point:', currentDragConnection.startPoint);
        
        if (targetNodeId && targetNodeId !== currentDragConnection.startNodeId) {
          // Check if target is valid
          if (!currentValidTargets.includes(targetNodeId)) {
            console.log('Invalid target - not in valid targets list');
            console.log('Current valid targets:', currentValidTargets);
            console.log('Target node ID:', targetNodeId);
            
            const targetNode = findNodeById(targetNodeId);
            if (currentDragConnection.startPoint === 'output') {
              // Check specific reasons for invalid output connection
              if (targetNode && connections.some(conn => conn.toNodeId === targetNodeId)) {
                message.warning('Target node already has an input connection. Each node can only have one input.');
              } else {
                message.warning('Can only connect to nodes in the next level without existing input connections');
              }
            } else {
              message.warning('Invalid connection target');
            }
            cleanup();
            return;
          }
          
          // Always create connection from output to input
          let fromNodeId, toNodeId;
          
          if (currentDragConnection.startPoint === 'output') {
            // Dragging from output point to another node
            fromNodeId = currentDragConnection.startNodeId;
            toNodeId = targetNodeId;
          } else {
            // Dragging from input point to another node
            fromNodeId = targetNodeId;
            toNodeId = currentDragConnection.startNodeId;
          }
          
          console.log('Connection attempt:', fromNodeId, '->', toNodeId);
          
          if (fromNodeId && toNodeId) {
            const fromNode = findNodeById(fromNodeId);
            const toNode = findNodeById(toNodeId);
            
            console.log('From node:', fromNode);
            console.log('To node:', toNode);
            
            if (fromNode && toNode) {
              console.log('Creating connection...');
              connectNodes(fromNodeId, toNodeId);
              setHasChanges(true);
              message.success(`Connected "${fromNode.title}" to "${toNode.title}"`);
            } else {
              console.log('Cannot connect - nodes not found');
              message.warning('Cannot connect these nodes');
            }
          } else {
            console.log('Invalid node IDs');
          }
        } else {
          console.log('Invalid target or same node');
        }
      } else {
        console.log('No node found under mouse');
      }
      
      cleanup();
    };

    const cleanup = () => {
      document.body.style.cursor = '';
      setDragConnection(null);
      setIsConnecting(false);
      setValidTargetNodes([]);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const canNodesConnect = (fromNode: NodeItem, toNode: NodeItem): boolean => {
    // Temporarily allow all connections for testing
    console.log('Checking connection between levels:', fromNode.level, 'and', toNode.level);
    return true;
    
    // Original logic (commented out for testing):
    // if (fromNode.level === null || toNode.level === null) return false;
    // return Math.abs(fromNode.level - toNode.level) === 1;
  };

  const connectNodes = (fromNodeId: string, toNodeId: string) => {
    // Check if this exact connection already exists
    const existingConnection = connections.find(conn => 
      conn.fromNodeId === fromNodeId && conn.toNodeId === toNodeId
    );
    
    console.log('connectNodes called with:', fromNodeId, '->', toNodeId);
    console.log('Existing connection:', existingConnection);
    console.log('Current connections before:', connections);
    
    if (!existingConnection) {
      // Check if target node already has an input connection
      const existingInputConnection = connections.find(conn => conn.toNodeId === toNodeId);
      
      if (existingInputConnection) {
        // Remove existing input connection first
        console.log('Removing existing input connection:', existingInputConnection);
        const fromNode = findNodeById(existingInputConnection.fromNodeId);
        const toNode = findNodeById(toNodeId);
        const newFromNode = findNodeById(fromNodeId);
        
        setConnections(prev => {
          const filteredConnections = prev.filter(conn => conn.id !== existingInputConnection.id);
          
          // Add the new connection
          const connectionId = `${fromNodeId}-${toNodeId}-${Date.now()}`;
          const newConnection: Connection = {
            id: connectionId,
            fromNodeId,
            toNodeId,
            fromPoint: 'output',
            toPoint: 'input'
          };
          
          const newConnections = [...filteredConnections, newConnection];
          console.log('Replaced input connection:', newConnections);
          return newConnections;
        });
        
        message.success(`Replaced connection: "${fromNode?.title}" → "${toNode?.title}" with "${newFromNode?.title}" → "${toNode?.title}"`);
      } else {
        // Create new connection normally
        const connectionId = `${fromNodeId}-${toNodeId}-${Date.now()}`;
        
        const newConnection: Connection = {
          id: connectionId,
          fromNodeId,
          toNodeId,
          fromPoint: 'output',
          toPoint: 'input'
        };
        
        console.log('Creating new connection:', newConnection);
        
        setConnections(prev => {
          const newConnections = [...prev, newConnection];
          console.log('New connections array:', newConnections);
          return newConnections;
        });
      }
      
      // Force re-render after a short delay
      setTimeout(() => {
        console.log('Connections after timeout:', connections);
      }, 100);
    } else {
      console.log('Connection already exists, skipping');
      message.warning('Connection already exists between these nodes');
    }
  };

  const deleteConnection = (connectionId: string) => {
    setConnections(prev => {
      const filteredConnections = prev.filter(conn => conn.id !== connectionId);
      console.log(`Deleted connection ${connectionId}`);
      return filteredConnections;
    });
    setHasChanges(true);
    message.success('Connection deleted');
  };

  const deleteInputConnections = (nodeId: string) => {
    setConnections(prev => {
      const filteredConnections = prev.filter(conn => conn.toNodeId !== nodeId);
      const deletedCount = prev.length - filteredConnections.length;
      if (deletedCount > 0) {
        console.log(`Deleted ${deletedCount} input connections for node ${nodeId}`);
        setHasChanges(true);
        message.success(`Deleted ${deletedCount} input connection${deletedCount > 1 ? 's' : ''}`);
      }
      return filteredConnections;
    });
  };

  const getAllNodes = (): NodeItem[] => {
    const allNodes: NodeItem[] = [...unassignedNodes];
    Object.values(treeStructure).forEach(levelNodes => {
      allNodes.push(...levelNodes);
    });
    return allNodes;
  };

  const findNodeById = (id: string): NodeItem | null => {
    // Search in tree structure
    for (const level in treeStructure) {
      const node = treeStructure[parseInt(level)].find(n => n.id === id);
      if (node) return node;
    }
    // Search in unassigned nodes
    const node = unassignedNodes.find(n => n.id === id);
    return node || null;
  };

  const updateNodeLevel = (nodeId: string, newLevel: number | null) => {
    const node = findNodeById(nodeId);
    if (!node) return;

    // Remove all connections related to this node
    setConnections(prev => {
      const filteredConnections = prev.filter(conn => 
        conn.fromNodeId !== nodeId && conn.toNodeId !== nodeId
      );
      const removedCount = prev.length - filteredConnections.length;
      if (removedCount > 0) {
        console.log(`Removed ${removedCount} connections for node ${nodeId} due to level change`);
      }
      return filteredConnections;
    });

    // Create updated node
    const updatedNode = { ...node, level: newLevel };

    // Remove from current location
    if (node.level === null || node.level === 0) {
      setUnassignedNodes(prev => prev.filter(n => n.id !== nodeId));
    } else {
      setTreeStructure(prev => ({
        ...prev,
        [node.level!]: prev[node.level!]?.filter(n => n.id !== nodeId) || []
      }));
    }

    // Add to new location
    if (newLevel === null || newLevel === 0) {
      setUnassignedNodes(prev => [...prev, updatedNode]);
    } else {
      setTreeStructure(prev => ({
        ...prev,
        [newLevel]: [...(prev[newLevel] || []), updatedNode]
      }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'red';
      case 'draft': return 'orange';
      default: return 'default';
    }
  };

  const calculateChanges = () => {
    const currentNodes = getAllNodes();
    const changes: Array<{
      nodeId: string;
      nodeTitle: string;
      changes: string[];
    }> = [];

    // Check level changes
    currentNodes.forEach(currentNode => {
      const originalNode = originalNodes.find(n => n.id === currentNode.id);
      if (originalNode) {
        console.log(`Checking node ${currentNode.title}:`);
        console.log(`  Original level: ${originalNode.level}`);
        console.log(`  Current level: ${currentNode.level}`);
        
        const nodeChanges: string[] = [];
        
        // Check level change
        if (originalNode.level !== currentNode.level) {
          console.log(`  Level changed: ${originalNode.level} → ${currentNode.level}`);
          nodeChanges.push(`Level: ${originalNode.level} → ${currentNode.level}`);
        }
        
        // Check nextNodeIds changes
        const currentNextNodes = connections
          .filter(conn => conn.fromNodeId === currentNode.id)
          .map(conn => conn.toNodeId)
          .sort();
        
        const originalNextNodes = [...originalNode.nextNodeIds].sort();
        
        if (JSON.stringify(currentNextNodes) !== JSON.stringify(originalNextNodes)) {
          const originalTitles = originalNextNodes.map(id => {
            const node = originalNodes.find(n => n.id === id);
            return node ? node.title : id;
          });
          const currentTitles = currentNextNodes.map(id => {
            const node = currentNodes.find(n => n.id === id);
            return node ? node.title : id;
          });
          
          nodeChanges.push(`Next nodes: [${originalTitles.join(', ')}] → [${currentTitles.join(', ')}]`);
        }
        
        if (nodeChanges.length > 0) {
          changes.push({
            nodeId: currentNode.id,
            nodeTitle: currentNode.title,
            changes: nodeChanges
          });
        }
      }
    });

    return changes;
  };

  const handleSaveTree = async () => {
    if (!id) {
      toast.error('Save Failed', 'LifeTree ID not found');
      return;
    }

    console.log('=== SAVE TREE DEBUG ===');
    console.log('Original nodes count:', originalNodes.length);
    console.log('Current nodes count:', getAllNodes().length);
    console.log('Original nodes:', originalNodes);
    console.log('Current nodes:', getAllNodes());
    
    const changes = calculateChanges();
    console.log('Calculated changes:', changes);
    
    if (changes.length === 0) {
      message.info('No changes detected');
      return;
    }

    // Create detailed change summary
    let changeText = 'The following changes will be saved:\n\n';
    changes.forEach((change, index) => {
      changeText += `${index + 1}. ${change.nodeTitle}:\n`;
      change.changes.forEach(changeDetail => {
        changeText += `   • ${changeDetail}\n`;
      });
      changeText += '\n';
    });

    // Show confirmation dialog
    if (!window.confirm(changeText + 'Do you want to proceed with saving these changes?')) {
      return;
    }

    setSaveLoading(true);
    try {
      // Prepare structure update request
      const currentNodes = getAllNodes();
      const structureUpdateData: StructureUpdateRequest = {
        nodes: currentNodes.map(node => {
          // Get current connections for this node
          const nextNodeIds = connections
            .filter(conn => conn.fromNodeId === node.id)
            .map(conn => conn.toNodeId);

          return {
            nodeId: node.id,
            level: node.level,
            nextNodeIds: nextNodeIds
          };
        })
      };

      console.log('Sending structure update request:', structureUpdateData);

      // Call API to update structure
      const updatedLifetree = await lifetreeApi.updateStructure(id, structureUpdateData);
      
      // Update state with fresh data from server
      setLifetree(updatedLifetree);
      organizeNodes(updatedLifetree.nodes);
      initializeConnections(updatedLifetree.nodes);
      
      // Update original state after successful save
      setOriginalNodes([...updatedLifetree.nodes]);
      const originalConnectionList: Connection[] = [];
      updatedLifetree.nodes.forEach(node => {
        node.nextNodeIds.forEach(nextNodeId => {
          originalConnectionList.push({
            id: `${node.id}-${nextNodeId}`,
            fromNodeId: node.id,
            toNodeId: nextNodeId,
            fromPoint: 'output',
            toPoint: 'input'
          });
        });
      });
      setOriginalConnections(originalConnectionList);
      
      setHasChanges(false);
      toast.success('Save Successful', 'Tree structure saved successfully!');
      
    } catch (error: any) {
      console.error('Error saving tree structure:', error);
      toast.error('Save Failed', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleAddNode = () => {
    setAddNodeModalVisible(true);
  };

  const handleAddNodeSubmit = async (nodeData: CreateNodeRequest) => {
    if (!id) return;
    
    setAddNodeLoading(true);
    try {
      const newNode = await lifetreeApi.createNode(id, nodeData);
      message.success('Node created successfully');
      setAddNodeModalVisible(false);
      
      // Add the new node to the appropriate location
      if (newNode.level === null || newNode.level === 0) {
        setUnassignedNodes(prev => [...prev, newNode]);
      } else {
        setTreeStructure(prev => ({
          ...prev,
          [newNode.level!]: [...(prev[newNode.level!] || []), newNode]
        }));
      }
    } catch (error: any) {
      toast.error('Create Node Failed', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setAddNodeLoading(false);
    }
  };

  const handleAddCharacter = () => {
    setAddCharacterModalVisible(true);
  };

  const handleAddCharacterCancel = () => {
    setAddCharacterModalVisible(false);
  };

  const handleEditLifetree = () => {
    if (!lifetree) return;
    editForm.setFieldsValue({
      title: lifetree.title,
      description: lifetree.description,
      imageUrl: lifetree.imageUrl,
      tags: lifetree.tags.join(', '),
      language: lifetree.language,
      outline: lifetree.outline,
      status: lifetree.status,
    });
    setEditModalVisible(true);
  };

  const handleGenerateDraftNodes = async () => {
    if (!id || !lifetree) return;
    if (!lifetree.outline) {
      toast.error('Thiếu Outline', 'Lifetree chưa có Outline. Hãy thêm Outline trước khi sinh Nodes.');
      return;
    }
    try {
      setGeneratingNodes(true);
      const updated = await lifetreeApi.generateDraftNodes(id, lifetree.language || 'en');
      setLifetree(updated);
      toast.success('Sinh Nodes thành công', 'AI đã tạo xong các Draft Nodes từ Outline!');
      fetchLifetreeDetail();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      toast.error('Lỗi', error?.response?.data?.message || 'Không thể sinh Nodes từ Outline');
    } finally {
      setGeneratingNodes(false);
    }
  };

  const handleSuggestOutline = async () => {
    console.log('handleSuggestOutline called', { id, lifetreeNodesCount: lifetree?.nodes?.length });
    // alert('Đang bắt đầu gợi ý Outline...'); // Tạm thời bỏ qua alert nếu log đã đủ
    
    if (!id || !lifetree) {
      console.log('Early return: id or lifetree missing', { id, lifetree });
      message.error('Thông tin LifeTree không khả dụng.');
      return;
    }

    try {
      // Validation: Check if all active nodes have a summary
      const nodes = lifetree.nodes || [];
      const activeNodes = nodes.filter(n => n.status?.toLowerCase() === 'active');
      console.log('Active nodes found:', activeNodes.length);
      
      // Check if there are active nodes at all
      if (activeNodes.length === 0) {
        console.log('No active nodes found');
        Modal.warning({
          title: 'Không có Node active',
          content: 'Lifetree cần có ít nhất một Node ở trạng thái active để AI có thể phân tích và gợi ý Outline.'
        });
        return;
      }

      const nodesMissingSummary = activeNodes.filter(n => !n.summary || n.summary.trim() === '');
      console.log('Nodes missing summary:', nodesMissingSummary.length);
      
      if (nodesMissingSummary.length > 0) {
        console.log('Validation failed: Some active nodes are missing summary');
        const nodeTitles = nodesMissingSummary.map(n => n.title).join(', ');
        
        // Thông báo nhanh bằng toast
        toast.error('Thiếu Summary', `Có ${nodesMissingSummary.length} node active chưa có tóm tắt.`);
        
        // Hiển thị Modal chi tiết
        Modal.error({
          title: 'Yêu cầu hoàn thiện tóm tắt Node',
          width: 500,
          content: (
            <div style={{ marginTop: '10px' }}>
              <p>AI cần nội dung tóm tắt (summary) của tất cả các Node đang hoạt động để có thể phác thảo Outline chính xác.</p>
              <p>Các Node sau đây đang thiếu summary:</p>
              <ul style={{ color: '#ff4d4f', maxHeight: '150px', overflowY: 'auto' }}>
                {nodesMissingSummary.map(n => <li key={n.id}><b>{n.title}</b></li>)}
              </ul>
              <p>Vui lòng vào chi tiết từng Node này và sử dụng tính năng "Tóm tắt bằng AI" trước nhé.</p>
            </div>
          ),
          okText: 'Đã hiểu'
        });
        return;
      }

      console.log('Calling suggestOutline API...');
      setSuggestingOutline(true);
      const result = await lifetreeApi.suggestOutline(id, lifetree.language || 'vi');
      console.log('API Result:', result);
      
      if (result && result.outline) {
        setSuggestedOutline(result.outline);
        setOutlineModalVisible(true);
      } else {
        throw new Error('AI không trả về kết quả outline hợp lệ.');
      }
    } catch (err: any) {
      console.error('Error in handleSuggestOutline:', err);
      toast.error('Lỗi', err.response?.data?.message || err.message || 'Có lỗi xảy ra khi gọi AI');
    } finally {
      setSuggestingOutline(false);
    }
  };

  const handleRefineOutline = async () => {
    if (!id || !lifetree || !outlineFeedback.trim()) return;

    try {
      setRefiningOutline(true);
      const result = await lifetreeApi.refineOutline(id, {
        currentOutline: suggestedOutline,
        feedback: outlineFeedback,
        language: lifetree.language || 'vi'
      });

      if (result && result.outline) {
        setSuggestedOutline(result.outline);
        setOutlineFeedback(''); // Clear feedback after successful refinement
        toast.success('Thành công', 'AI đã điều chỉnh Outline dựa trên góp ý của bạn!');
      } else {
        throw new Error('AI không trả về kết quả outline hợp lệ.');
      }
    } catch (err: any) {
      console.error('Error in handleRefineOutline:', err);
      toast.error('Lỗi', err.response?.data?.message || err.message || 'Có lỗi xảy ra khi gọi AI');
    } finally {
      setRefiningOutline(false);
    }
  };

  const handleApplySuggestedOutline = async () => {
    if (!id || !lifetree) return;
    
    try {
      setEditLoading(true);
      const updateData: UpdateLifetreeRequest = {
        title: lifetree.title,
        description: lifetree.description,
        imageUrl: lifetree.imageUrl,
        tags: lifetree.tags,
        language: lifetree.language,
        outline: suggestedOutline,
        status: lifetree.status,
      };

      const updated = await lifetreeApi.updateLifetree(id, updateData);
      setLifetree(updated);
      toast.success('Cập nhật thành công', 'Outline mới đã được áp dụng cho Lifetree!');
      setOutlineModalVisible(false);
    } catch (error: any) {
      toast.error('Cập nhật thất bại', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleSuggestNextNodes = async (node: NodeItem) => {
    if (!id || !lifetree) return;
    
    try {
      setParentNodeForSuggestions(node);
      setSuggestingNextNodes(true);
      
      const result = await lifetreeApi.suggestNextNodes(id, node.id, lifetree.language || 'vi');
      
      if (result && result.suggestions) {
        setNodeSuggestions(result.suggestions);
        setNodeSuggestionsModalVisible(true);
      } else {
        throw new Error('AI không trả về kết quả gợi ý hợp lệ.');
      }
    } catch (err: any) {
      console.error('Error in handleSuggestNextNodes:', err);
      toast.error('Lỗi', err.response?.data?.message || err.message || 'Có lỗi xảy ra khi gọi AI gợi ý Node');
    } finally {
      setSuggestingNextNodes(false);
    }
  };

  const handleSelectNextNodeSuggestion = async (suggestion: NextNodeSuggestion) => {
    if (!id || !lifetree || !parentNodeForSuggestions) return;

    try {
      setApplyingSuggestion(true);
      
      // 1. Create a new draft node based on suggestion
      const newNodeData: CreateNodeRequest = {
        title: suggestion.title,
        direction: suggestion.direction,
        characterStateIds: [], // Start with empty, user will add later
        level: (parentNodeForSuggestions.level || 0) + 1,
        tags: [],
        requiredExp: 0
      };

      const newNode = await lifetreeApi.createNode(id, newNodeData);
      
      // 2. Automatically connect parent to this new node
      const currentConnections = [...connections];
      const newConnection: Connection = {
        id: `${parentNodeForSuggestions.id}-${newNode.id}-${Date.now()}`,
        fromNodeId: parentNodeForSuggestions.id,
        toNodeId: newNode.id,
        fromPoint: 'output',
        toPoint: 'input'
      };
      
      const updatedConnections = [...currentConnections, newConnection];
      
      // 3. Save the structure change immediately
      const structureUpdateData: StructureUpdateRequest = {
        nodes: lifetree.nodes.map(node => {
          let nextIds = [...node.nextNodeIds];
          if (node.id === parentNodeForSuggestions.id) {
            nextIds = Array.from(new Set([...nextIds, newNode.id]));
          }
          
          return {
            nodeId: node.id,
            level: node.level,
            nextNodeIds: nextIds
          };
        })
      };
      
      // Add the new node to structure update as well
      structureUpdateData.nodes.push({
        nodeId: newNode.id,
        level: newNode.level,
        nextNodeIds: []
      });

      await lifetreeApi.updateStructure(id, structureUpdateData);
      
      toast.success('Thành công', `Đã tạo Node mới "${suggestion.title}" và kết nối từ Node cha.`);
      setNodeSuggestionsModalVisible(false);
      
      // Refresh tree
      fetchLifetreeDetail();
      
    } catch (err: any) {
      console.error('Error in handleSelectNextNodeSuggestion:', err);
      toast.error('Lỗi', err.response?.data?.message || err.message || 'Có lỗi xảy ra khi tạo Node từ gợi ý');
    } finally {
      setApplyingSuggestion(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!id || !lifetree) return;
    try {
      const values = await editForm.validateFields();
      setEditLoading(true);
      
      const updateData: UpdateLifetreeRequest = {
        title: values.title,
        description: values.description,
        imageUrl: values.imageUrl,
        tags: values.tags ? values.tags.split(',').map((t: string) => t.trim()).filter((t: string) => t !== '') : [],
        language: values.language,
        outline: values.outline,
        status: values.status,
      };

      const updated = await lifetreeApi.updateLifetree(id, updateData);
      setLifetree(updated);
      toast.success('Update Successful', 'Lifetree updated successfully!');
      setEditModalVisible(false);
    } catch (error: any) {
      console.error('Error updating lifetree:', error);
      toast.error('Update Failed', error.response?.data?.messages?.[0] || error.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleAddCharacterSubmit = async (data: CreateCharacterRequest) => {
    if (!id) return;
    
    setAddCharacterLoading(true);
    try {
      const newCharacter = await lifetreeApi.createCharacter(id, data);
      message.success('Tạo nhân vật thành công!');
      
      // Close modal
      setAddCharacterModalVisible(false);
      
      // Redirect to character detail page
      navigate(`/lifetrees/${id}/characters/${newCharacter.id}`);
    } catch (error: any) {
      message.error('Tạo nhân vật thất bại: ' + (error.response?.data?.messages?.[0] || error.message));
      throw error; // Re-throw to let modal handle the error
    } finally {
      setAddCharacterLoading(false);
    }
  };

  const handleNodeDetail = (nodeId: string) => {
    if (id) {
      navigate(`/lifetrees/${id}/nodes/${nodeId}`);
    }
  };

  const handleCharacterDetail = (characterId: string) => {
    if (id) {
      const path = `/lifetrees/${id}/characters/${characterId}`;
      window.open(path, '_blank');
    }
  };

  const handleOpenCoverPicker = async () => {
    if (!id || !lifetree) return;
    setCoverSelectedUrl(lifetree.imageUrl || null);
    setCoverPickerOpen(true);
    setCoverPickerLoading(true);
    try {
      const nodeDetails: NodeDetailResponse[] = await Promise.all(
        lifetree.nodes.map(n => lifetreeApi.getNodeDetail(id, n.id))
      );
      const allImages: typeof coverPickerImages = [];
      for (const nd of nodeDetails) {
        for (const cs of nd.characterStates) {
          for (const img of cs.images || []) {
            allImages.push({
              url: img.url,
              label: cs.name,
              stateName: cs.name,
              avatar: cs.imageUrl,
              width: img.width,
              height: img.height,
              nodeTitle: nd.title,
            });
          }
        }
        for (const sc of nd.scenarios) {
          for (const img of sc.images || []) {
            allImages.push({
              url: img.url,
              label: sc.name,
              stateName: undefined,
              avatar: undefined,
              width: img.width,
              height: img.height,
              nodeTitle: nd.title,
            });
          }
        }
      }
      setCoverPickerImages(allImages);
    } catch (err: any) {
      toast.error('Lỗi', 'Không thể tải ảnh');
    } finally {
      setCoverPickerLoading(false);
    }
  };

  const handleConfirmCover = async () => {
    if (!id || !lifetree || !coverSelectedUrl) return;
    setSettingCover(true);
    try {
      const updateData: UpdateLifetreeRequest = {
        title: lifetree.title,
        description: lifetree.description,
        imageUrl: coverSelectedUrl,
        tags: lifetree.tags,
        language: lifetree.language,
        outline: lifetree.outline,
        status: lifetree.status,
      };
      const updated = await lifetreeApi.updateLifetree(id, updateData);
      setLifetree(updated);
      setCoverPickerOpen(false);
      toast.success('Cập nhật thành công', 'Đã đặt ảnh bìa cho LifeTree');
    } catch (err: any) {
      toast.error('Lỗi', err.response?.data?.message || err.message);
    } finally {
      setSettingCover(false);
    }
  };

  const handleActivateLifetree = async () => {
    if (!id) return;

    try {
      setActivatingLifetree(true);
      
      const updatedLifetree = await lifetreeApi.activateLifetree(id);
      
      // Update state with activated lifetree
      setLifetree(updatedLifetree);
      
      toast.success('LifeTree Activated', 'LifeTree has been successfully activated!');
    } catch (error: any) {
      toast.error('Activation Failed', error.response?.data?.message || error.message);
    } finally {
      setActivatingLifetree(false);
    }
  };

  const renderTreeView = () => {
    const allNodes = [
      ...unassignedNodes,
      ...Object.values(treeStructure).flat()
    ];

    return (
      <div style={{ position: 'relative' }} className="tree-container">
        {/* Connection Lines */}
        {connections.length > 0 && (
          <>
            {connections.map(connection => {
              console.log('Rendering connection:', connection.id, connection.fromNodeId, '->', connection.toNodeId);
              const fromElement = document.querySelector(`[data-node-id="${connection.fromNodeId}"][data-connection-type="output"]`) as HTMLElement;
              const toElement = document.querySelector(`[data-node-id="${connection.toNodeId}"][data-connection-type="input"]`) as HTMLElement;
              
              if (!fromElement || !toElement) {
                console.log('Missing elements for connection:', connection.id, 'From:', fromElement, 'To:', toElement);
                return null;
              }
              
              return (
                <ConnectionLine 
                  key={connection.id} 
                  connection={connection} 
                  fromElement={fromElement}
                  toElement={toElement}
                />
              );
            })}
          </>
        )}
        
        {/* Drag Connection Line */}
        {dragConnection && (
          <svg style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 10 }}>
            <defs>
              <marker 
                id="drag-arrowhead" 
                markerWidth="8" 
                markerHeight="6" 
                refX="7" 
                refY="3" 
                orient="auto"
              >
                <polygon points="0 0, 8 3, 0 6" fill="#ff4d4f" />
              </marker>
            </defs>
            <path
              d={`M ${dragConnection.startX} ${dragConnection.startY} Q ${(dragConnection.startX + dragConnection.currentX) / 2} ${dragConnection.startY} ${(dragConnection.startX + dragConnection.currentX) / 2} ${(dragConnection.startY + dragConnection.currentY) / 2} Q ${(dragConnection.startX + dragConnection.currentX) / 2} ${dragConnection.currentY} ${dragConnection.currentX} ${dragConnection.currentY}`}
              stroke="#ff4d4f"
              strokeWidth="2"
              fill="none"
              strokeDasharray="5,5"
              markerEnd="url(#drag-arrowhead)"
            />
          </svg>
        )}

        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAddNode}
            >
              Add Node
            </Button>
            <Button 
              icon={<RobotOutlined />}
              onClick={handleGenerateDraftNodes}
              loading={generatingNodes}
              disabled={!lifetree?.outline}
              title={!lifetree?.outline ? 'Lifetree chưa có Outline' : 'Sinh Draft Nodes từ Outline'}
            >
              Sinh Nodes từ Outline
            </Button>
            <Button 
              type={hasChanges ? 'primary' : 'default'} 
              icon={<SaveOutlined />} 
              onClick={handleSaveTree}
              disabled={!hasChanges}
              loading={saveLoading}
            >
              Save Tree {hasChanges && '*'}
            </Button>
            <Button 
              onClick={() => {
                console.log('Current connections state:', connections);
                console.log('Connections count:', connections.length);
              }}
            >
              Debug Connections ({connections.length})
            </Button>
          </div>

          <div style={{ display: 'flex', overflowX: 'auto', paddingBottom: 16 }}>
            {/* Level columns */}
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
              <DroppableColumn
                key={level}
                level={level}
                nodes={treeStructure[level] || []}
                title={`Level ${level}`}
                onConnectionStart={handleConnectionStart}
                onDeleteConnections={deleteInputConnections}
                connections={connections}
                validTargetNodes={validTargetNodes}
                onNodeDetail={handleNodeDetail}
                onSuggestNextNodes={handleSuggestNextNodes}
                suggestingNextNodes={suggestingNextNodes}
                parentNodeForSuggestions={parentNodeForSuggestions?.id}
              />
            ))}
          </div>

          {/* Unassigned nodes section */}
          <Divider>Unassigned Nodes</Divider>
          <DroppableColumn
            level="unassigned"
            nodes={unassignedNodes}
            title="Unassigned Nodes"
            onConnectionStart={handleConnectionStart}
            onDeleteConnections={deleteInputConnections}
            connections={connections}
            validTargetNodes={validTargetNodes}
            onNodeDetail={handleNodeDetail}
            onSuggestNextNodes={handleSuggestNextNodes}
            suggestingNextNodes={suggestingNextNodes}
            parentNodeForSuggestions={parentNodeForSuggestions?.id}
          />

          <DragOverlay>
            {activeNode ? (
              <div 
                style={{ 
                  width: 120, 
                  height: 100,
                  opacity: 0.8, 
                  transform: 'rotate(5deg)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '2px solid #d9d9d9',
                  position: 'relative',
                  backgroundImage: activeNode.imageUrl ? `url(${activeNode.imageUrl})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'top center',
                  backgroundColor: activeNode.imageUrl ? 'transparent' : '#f0f0f0',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                }}
              >
                {/* Level tag - Top left corner */}
                {activeNode.level !== null && activeNode.level > 0 && (
                  <Tag 
                    style={{ 
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      fontSize: '10px',
                      padding: '2px 6px',
                      lineHeight: '1.2',
                      height: 'auto',
                      margin: 0,
                      backgroundColor: '#1890ff',
                      color: 'white',
                      border: 'none',
                      fontWeight: 'bold',
                      zIndex: 2,
                    }}
                  >
                    {toRoman(activeNode.level)}
                  </Tag>
                )}

                {/* Title overlay at bottom */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                    padding: '8px 6px 4px 6px',
                    textAlign: 'center',
                  }}
                >
                  <Text 
                    style={{ 
                      fontSize: '10px', 
                      color: 'white',
                      lineHeight: '1.2',
                      display: 'block',
                      wordBreak: 'break-word',
                      fontWeight: 500,
                      textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                    }}
                  >
                    {activeNode.title.length > 18 ? `${activeNode.title.substring(0, 18)}...` : activeNode.title}
                  </Text>
                </div>

                {/* No image placeholder */}
                {!activeNode.imageUrl && (
                  <div style={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#999',
                    fontSize: '10px',
                    pointerEvents: 'none',
                  }}>
                    No Image
                  </div>
                )}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    );
  };

  const renderBasicInfo = () => (
    <Card
      title="Basic Information"
      extra={
        <Space>
          <Button 
            icon={<PictureOutlined />} 
            onClick={() => {
              setImageManagerOpen(true);
              fetchLifetreeImages();
            }}
          >
            Quản lý ảnh
          </Button>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={handleEditLifetree}
          >
            Edit
          </Button>
        </Space>
      }
    >
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Tooltip title="Chọn ảnh bìa">
            <div
              onClick={handleOpenCoverPicker}
              style={{
                width: '100%',
                minHeight: 200,
                borderRadius: 8,
                border: '1px dashed #d9d9d9',
                background: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              {lifetree?.imageUrl ? (
                <>
                  <img
                    src={lifetree.imageUrl}
                    alt={lifetree.title}
                    style={{ width: '100%', maxHeight: 300, objectFit: 'contain' }}
                  />
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(0,0,0,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: 0, transition: 'opacity 0.2s',
                  }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
                  >
                    <EditOutlined style={{ color: '#fff', fontSize: 24 }} />
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', color: '#bfbfbf', padding: 24 }}>
                  <PictureOutlined style={{ fontSize: 32 }} />
                  <div style={{ fontSize: 12, marginTop: 8 }}>Chọn ảnh bìa</div>
                </div>
              )}
            </div>
          </Tooltip>
        </Col>
        <Col span={16}>
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Title">
              <Text strong>{lifetree?.title}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(lifetree?.status || '')}>{lifetree?.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {lifetree?.description || 'No description'}
            </Descriptions.Item>
            <Descriptions.Item label="Tags">
              {lifetree?.tags?.map(tag => (
                <Tag key={tag}>{tag}</Tag>
              )) || 'No tags'}
            </Descriptions.Item>
            <Descriptions.Item label="Outline">
              <div style={{ position: 'relative' }}>
                {lifetree?.outline ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{lifetree.outline}</div>
                ) : (
                  <Text type="secondary" italic>No outline</Text>
                )}
                <div style={{ marginTop: 8 }}>
                  <Button 
                    size="small" 
                    icon={<RobotOutlined />} 
                    onClick={handleSuggestOutline}
                    loading={suggestingOutline}
                  >
                    Gợi ý Outline bằng AI
                  </Button>
                </div>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Created By">
              {lifetree?.createdBy}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {lifetree?.createdAt ? new Date(lifetree.createdAt).toLocaleString() : 'Unknown'}
            </Descriptions.Item>
            <Descriptions.Item label="Modified At">
              {lifetree?.modifiedAt ? new Date(lifetree.modifiedAt).toLocaleString() : 'Unknown'}
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>
    </Card>
  );

  const renderCharacters = () => (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCharacter}>
          Add Character
        </Button>
      </div>
      
      {lifetree?.characters && lifetree.characters.length > 0 ? (
        <Row gutter={[16, 16]}>
          {lifetree.characters.map(character => (
            <Col key={character.id} xs={12} sm={6} md={4} lg={3}>
              <div
                style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #d9d9d9',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  backgroundImage: character.imageUrl ? `url(${character.imageUrl})` : 'none',
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundColor: '#f0f0f0',
                }}
                onClick={() => handleCharacterDetail(character.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                {/* No image placeholder */}
                {!character.imageUrl && (
                  <div style={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#999',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}>
                    No Image
                  </div>
                )}

                {/* Type badge - Top left */}
                <Tag 
                  style={{ 
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    margin: 0,
                    fontWeight: 'bold',
                    fontSize: '11px',
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                  color={character.type === 'main' ? 'gold' : 'blue'}
                >
                  {character.type.toUpperCase()}
                </Tag>

                {/* Status badge - Top right */}
                <Tag 
                  style={{ 
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    margin: 0,
                    fontWeight: 'bold',
                    fontSize: '11px',
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                  color={getStatusColor(character.status)}
                >
                  {character.status.toUpperCase()}
                </Tag>

                {/* Character info overlay - Bottom */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                    padding: '16px 12px 8px 12px',
                    color: 'white',
                  }}
                >
                  <div style={{ marginBottom: 4 }}>
                    <Text 
                      style={{ 
                        fontSize: '16px', 
                        color: 'white',
                        fontWeight: 600,
                        display: 'block',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                        lineHeight: '1.2',
                      }}
                      title={character.name}
                    >
                      {character.name.length > 20 ? `${character.name.substring(0, 20)}...` : character.name}
                    </Text>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text 
                      style={{ 
                        fontSize: '12px', 
                        color: 'rgba(255,255,255,0.9)',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                      }}
                    >
                      {character.states.length} state{character.states.length !== 1 ? 's' : ''}
                    </Text>
                    
                    <Button
                      type="text"
                      size="small"
                      icon={<EyeOutlined />}
                      style={{
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: '4px',
                        height: '24px',
                        minWidth: '24px',
                        padding: '0 6px',
                        fontSize: '12px',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCharacterDetail(character.id);
                      }}
                      title="View Character Detail"
                    />
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <Empty description="No characters found" />
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!lifetree) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>LifeTree not found</Title>
        <Button onClick={() => navigate('/lifetrees')}>Back to LifeTrees</Button>
      </div>
    );
  }

  // Compute activate conditions
  const ltHasImage = !!lifetree.imageUrl;
  const nodeMap = new Map(lifetree.nodes.map(n => [n.id, n]));
  const checkChain = (): boolean => {
    // Find node at level 1
    const rootNode = lifetree.nodes.find(n => n.level === 1);
    if (!rootNode || rootNode.status !== 'active') return false;
    let count = 1;
    let current = rootNode;
    while (count < 3) {
      const nextActive = current.nextNodeIds
        .map(id => nodeMap.get(id))
        .find(n => n && n.status === 'active');
      if (!nextActive) return false;
      current = nextActive;
      count++;
    }
    return true;
  };
  const ltHasChain = checkChain();
  const ltCanActivate = ltHasImage && ltHasChain;

  const ltActivateTooltip = (
    <div style={{ minWidth: 240 }}>
      <div style={{ marginBottom: 6, fontWeight: 600 }}>Điều kiện kích hoạt LifeTree:</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {ltHasImage
          ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
          : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
        <span>LifeTree có ảnh bìa</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {ltHasChain
          ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
          : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
        <span>Có ít nhất 3 node liên tiếp đã active từ node đầu tiên</span>
      </div>
    </div>
  );

  return (
    <div>
      <PageTitleSetter title={lifetree.title} />
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        paddingBottom: 16,
        borderBottom: '1px solid #f0f0f0',
      }}>
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/lifetrees')}
            style={{ padding: '0 4px' }}
          />
          <div>
            <Space align="center" size={8}>
              <Title level={4} style={{ margin: 0 }}>{lifetree.title}</Title>
              <Tag color={getStatusColor(lifetree.status)} style={{ margin: 0 }}>
                {lifetree.status.toUpperCase()}
              </Tag>
            </Space>
          </div>
        </Space>

        {lifetree.status === 'draft' && (
          <Popconfirm
            title="Kích hoạt LifeTree?"
            description="LifeTree sẽ chuyển sang active và người chơi có thể truy cập."
            onConfirm={handleActivateLifetree}
            okText="Kích hoạt"
            cancelText="Hủy"
            disabled={!ltCanActivate}
          >
            <Tooltip title={ltActivateTooltip} overlayStyle={{ maxWidth: 340 }}>
              <span style={{ display: 'inline-block', cursor: ltCanActivate ? 'pointer' : 'not-allowed' }}>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  loading={activatingLifetree}
                  disabled={!ltCanActivate}
                  style={ltCanActivate
                    ? { backgroundColor: '#52c41a', borderColor: '#52c41a', pointerEvents: 'none' }
                    : { pointerEvents: 'none' }}
                >
                  Kích hoạt LifeTree
                </Button>
              </span>
            </Tooltip>
          </Popconfirm>
        )}
        {lifetree.status === 'active' && (
          <Tag color="green" icon={<PlayCircleOutlined />} style={{ padding: '4px 10px', fontSize: '13px' }}>
            Đã kích hoạt
          </Tag>
        )}
      </div>

      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane tab="Basic Information" key="1">
          {renderBasicInfo()}
        </TabPane>
        <TabPane tab="Tree Structure" key="2">
          {renderTreeView()}
        </TabPane>
        <TabPane tab="Characters" key="3">
          {renderCharacters()}
        </TabPane>
      </Tabs>

      <AddNodeModal
        visible={addNodeModalVisible}
        onCancel={() => setAddNodeModalVisible(false)}
        onSubmit={handleAddNodeSubmit}
        loading={addNodeLoading}
        characters={lifetree.characters}
      />

      <CreateCharacterModal
        visible={addCharacterModalVisible}
        onCancel={handleAddCharacterCancel}
        onSubmit={handleAddCharacterSubmit}
        loading={addCharacterLoading}
      />

      {/* Edit Lifetree Modal */}
      <Modal
        title="Edit Lifetree Information"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleEditSubmit}
        confirmLoading={editLoading}
        width={800}
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: 'Please enter title' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="language"
                label="Language"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="en">English</Option>
                  <Option value="vi">Vietnamese</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="imageUrl"
            label="Image URL"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="tags"
            label="Tags (comma separated)"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="outline"
            label="Outline (Mạch truyện tổng thể)"
          >
            <TextArea rows={6} placeholder="Phác thảo mạch truyện tổng thể của Lifetree..." />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
          >
            <Radio.Group>
              <Radio value="draft">Draft</Radio>
              <Radio value="active">Active</Radio>
              <Radio value="inactive">Inactive</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* Suggested Outline Review Modal */}
      <Modal
        title={
          <Space>
            <RobotOutlined style={{ color: '#1890ff' }} />
            <span>AI Đề xuất Outline cho Lifetree</span>
          </Space>
        }
        open={outlineModalVisible}
        onCancel={() => setOutlineModalVisible(false)}
        onOk={handleApplySuggestedOutline}
        confirmLoading={editLoading}
        width={900}
        okText="Áp dụng Outline này"
        cancelText="Hủy"
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ padding: '12px', background: '#e6f7ff', borderRadius: '4px', borderLeft: '4px solid #1890ff', marginBottom: 16 }}>
            <Text>AI đã phân tích các Node <b>active</b> hiện có để tái cấu trúc và đề xuất một mạch truyện (Outline) hoàn chỉnh gồm 10 giai đoạn. Bạn có thể chỉnh sửa nội dung bên dưới hoặc gửi góp ý để AI viết lại.</Text>
          </div>
          <TextArea 
            rows={15} 
            value={suggestedOutline} 
            onChange={(e) => setSuggestedOutline(e.target.value)}
            placeholder="Nội dung Outline đề xuất từ AI..."
            style={{ fontFamily: 'monospace', fontSize: '13px' }}
          />
        </div>

        <Divider orientation="left">Góp ý để AI viết lại</Divider>
        <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
          <TextArea 
            rows={3} 
            value={outlineFeedback} 
            onChange={(e) => setOutlineFeedback(e.target.value)}
            placeholder="Ví dụ: Hãy kéo dài câu chuyện đến năm 60 tuổi, thêm các tình tiết bi kịch ở giai đoạn trung niên..."
          />
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <Button 
              type="primary"
              ghost
              icon={<RobotOutlined />} 
              onClick={handleRefineOutline} 
              loading={refiningOutline}
              disabled={!outlineFeedback.trim()}
            >
              Yêu cầu AI viết lại (Regenerate)
            </Button>
          </div>
        </div>
      </Modal>

      {/* Suggested Next Nodes Modal */}
      <Modal
        title={
          <Space>
            <RobotOutlined style={{ color: '#1890ff' }} />
            <span>AI Đề xuất Node tiếp theo</span>
          </Space>
        }
        open={nodeSuggestionsModalVisible}
        onCancel={() => setNodeSuggestionsModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ padding: '12px', background: '#e6f7ff', borderRadius: '4px', borderLeft: '4px solid #1890ff', marginBottom: 16 }}>
            <Text>Dựa trên nội dung của Node <b>{parentNodeForSuggestions?.title}</b>, AI đề xuất các hướng đi tiếp theo như sau. Chọn một gợi ý để tạo Node mới và kết nối tự động.</Text>
          </div>
          
          <Row gutter={[16, 16]}>
            {nodeSuggestions.map((suggestion, index) => (
              <Col span={24} key={index}>
                <Card 
                  hoverable 
                  style={{ border: '1px solid #d9d9d9' }}
                  actions={[
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />} 
                      onClick={() => handleSelectNextNodeSuggestion(suggestion)}
                      loading={applyingSuggestion}
                    >
                      Chọn gợi ý này
                    </Button>
                  ]}
                >
                  <Card.Meta
                    title={<Text strong style={{ fontSize: '16px' }}>{suggestion.title}</Text>}
                    description={
                      <div style={{ marginTop: 8 }}>
                        <div style={{ marginBottom: 12 }}>
                          <Text strong>Định hướng nội dung:</Text>
                          <p style={{ color: '#555', marginTop: 4 }}>{suggestion.direction}</p>
                        </div>
                        <div style={{ padding: '8px 12px', background: '#fff7e6', borderRadius: '4px', borderLeft: '4px solid #ffa940' }}>
                          <Text type="secondary" style={{ color: '#d46b08', display: 'block', marginBottom: 4, fontSize: '12px' }}>
                            <ThunderboltOutlined /> Tại sao nên chọn hướng này?
                          </Text>
                          <Text style={{ color: '#874d00', fontSize: '12px' }}>
                            {suggestion.reason}
                          </Text>
                        </div>
                      </div>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </Modal>

      {/* Cover Image Picker Modal */}
      <Modal
        title={
          <Space>
            <PictureOutlined style={{ color: '#1890ff' }} />
            <span>Chọn ảnh bìa LifeTree</span>
          </Space>
        }
        open={coverPickerOpen}
        onCancel={() => setCoverPickerOpen(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setCoverPickerOpen(false)}>Hủy</Button>,
          <Button
            key="confirm"
            type="primary"
            disabled={!coverSelectedUrl || coverSelectedUrl === lifetree?.imageUrl}
            loading={settingCover}
            onClick={handleConfirmCover}
          >
            Đặt làm ảnh bìa
          </Button>,
        ]}
      >
        {coverPickerLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin tip="Đang tải ảnh..." />
          </div>
        ) : coverPickerImages.length === 0 ? (
          <Empty description="Chưa có ảnh nào trong LifeTree" style={{ margin: '24px 0' }} />
        ) : (
          <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '4px 2px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
              {coverPickerImages.map((img, idx) => {
                const isSelected = coverSelectedUrl === img.url;
                const maxW = 200, maxH = 180;
                let dispW = maxW, dispH = maxH;
                if (img.width && img.height) {
                  const ratio = img.width / img.height;
                  if (ratio >= 1) { dispW = maxW; dispH = Math.round(maxW / ratio); }
                  else { dispH = maxH; dispW = Math.round(maxH * ratio); }
                }
                const resLabel = img.width && img.height ? `${img.width}×${img.height}` : '';
                return (
                  <div
                    key={idx}
                    onClick={() => setCoverSelectedUrl(img.url)}
                    style={{
                      position: 'relative',
                      width: dispW,
                      cursor: 'pointer',
                      borderRadius: 8,
                      border: isSelected ? '2px solid #1890ff' : '2px solid transparent',
                      boxShadow: isSelected ? '0 0 0 2px #91caff' : '0 1px 4px rgba(0,0,0,0.12)',
                      overflow: 'hidden',
                      background: '#f5f5f5',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                    }}
                  >
                    <img
                      src={img.url}
                      alt={img.label}
                      style={{ width: dispW, height: dispH, objectFit: 'contain', display: 'block', background: '#f0f0f0' }}
                    />
                    <div style={{ padding: '5px 8px', background: '#fff' }}>
                      <Space size={4} style={{ width: '100%' }}>
                        {img.avatar !== undefined && (
                          <img
                            src={img.avatar || undefined}
                            alt=""
                            style={{ width: 14, height: 14, borderRadius: '50%', flexShrink: 0, background: '#eee' }}
                          />
                        )}
                        <Text style={{ fontSize: 11, flex: 1, minWidth: 0 }} ellipsis={{ tooltip: img.label }}>
                          {img.label}
                        </Text>
                      </Space>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 10 }} ellipsis={{ tooltip: img.nodeTitle }}>
                          {img.nodeTitle}
                        </Text>
                        {resLabel && (
                          <Text type="secondary" style={{ fontSize: 10, marginLeft: 'auto' }}>{resLabel}</Text>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <div style={{
                        position: 'absolute', top: 6, right: 6,
                        background: '#1890ff', borderRadius: '50%',
                        width: 20, height: 20,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <CheckCircleOutlined style={{ color: '#fff', fontSize: 12 }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Modal>

      {/* Lifetree Image Manager Modal */}
      <Modal
        title={
          <Space>
            <PictureOutlined style={{ color: '#1890ff' }} />
            <span>Quản lý ảnh Lifetree (Poster/Cover)</span>
          </Space>
        }
        open={imageManagerOpen}
        onCancel={() => {
          setImageManagerOpen(false);
          setAiConcept(null);
          setRepresentativeNodeId(undefined);
        }}
        width={1000}
        footer={null}
      >
        <Tabs defaultActiveKey="list">
          <TabPane tab="Ảnh đã tạo" key="list">
            {fetchingLifetreeImages ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            ) : lifetreeImages.length === 0 ? (
              <Empty description="Chưa có ảnh nào" />
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                {lifetreeImages.map((img) => (
                  <Card
                    key={img.id}
                    hoverable
                    style={{ width: 300 }}
                    cover={
                      <div style={{ aspectRatio: '3 / 2', overflow: 'hidden', background: '#f0f0f0' }}>
                        <img alt="lifetree" src={img.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    }
                    actions={[
                      <Button 
                        type="link" 
                        icon={<CheckCircleOutlined />} 
                        disabled={lifetree?.imageUrl === img.url}
                        onClick={() => handleSelectLifetreeCover(img.url)}
                      >
                        {lifetree?.imageUrl === img.url ? 'Đang dùng' : 'Dùng làm bìa'}
                      </Button>
                    ]}
                  >
                    <Card.Meta 
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary" style={{ fontSize: 11 }}>{new Date(img.createdAt).toLocaleString()}</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>{img.width}x{img.height}</Text>
                        </Space>
                      } 
                    />
                  </Card>
                ))}
              </div>
            )}
          </TabPane>
          <TabPane tab="Tạo ảnh mới bằng AI" key="create">
            <div style={{ padding: '0 8px' }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>1. Chọn Node đại diện cho Lifetree:</Text>
                <div style={{ marginTop: 8 }}>
                  <Select
                    placeholder="Chọn một Node tiêu biểu..."
                    style={{ width: '100%' }}
                    value={representativeNodeId}
                    onChange={(val) => {
                      setRepresentativeNodeId(val);
                      setAiConcept(null);
                    }}
                  >
                    {lifetree?.nodes.filter(n => n.level !== null && n.level > 0).map(node => (
                      <Option key={node.id} value={node.id}>
                        Lvl {node.level}: {node.title}
                      </Option>
                    ))}
                  </Select>
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                    AI sẽ dùng bối cảnh và nhân vật trong Node này để tạo ảnh Poster.
                  </Text>
                </div>
              </div>

              {representativeNodeId && (
                <div style={{ marginBottom: 24 }}>
                  <Button 
                    type="primary" 
                    icon={<RobotOutlined />} 
                    onClick={handleGenerateConcept}
                    loading={isGeneratingConcept}
                  >
                    AI đề xuất concept
                  </Button>
                </div>
              )}

              {aiConcept && (
                <Card title="Đề xuất từ AI" size="small" style={{ marginBottom: 16, background: '#f9f9f9' }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Mô tả nội dung (Việt):</Text>
                    <div style={{ padding: '4px 8px', background: '#fff', borderRadius: 4, marginTop: 4, border: '1px solid #eee' }}>
                      <Text style={{ fontSize: 13 }}>{aiConcept.refinedVisualDescription || aiConcept.description}</Text>
                    </div>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Nhân vật xuất hiện:</Text>
                    <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {aiConcept.involvedCharacterIds?.map((charId: string) => {
                        const char = lifetree?.characters.find(c => c.id === charId);
                        return (
                          <Tag key={charId} icon={<UserOutlined />} color="blue">
                            {char?.name || charId}
                          </Tag>
                        );
                      })}
                      {(!aiConcept.involvedCharacterIds || aiConcept.involvedCharacterIds.length === 0) && (
                        <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>Không xác định</Text>
                      )}
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Technical Prompt (English):</Text>
                    <div style={{ padding: '4px 8px', background: '#fff', borderRadius: 4, marginTop: 4, border: '1px solid #eee' }}>
                      <Text style={{ fontSize: 11, fontStyle: 'italic' }}>{aiConcept.technicalPrompt}</Text>
                    </div>
                  </div>
                  
                  <Divider />
                  
                  <div style={{ textAlign: 'center' }}>
                    <Button 
                      type="primary" 
                      size="large" 
                      icon={<ThunderboltOutlined />} 
                      onClick={handleGenerateImage}
                      loading={isGeneratingImage}
                      style={{ background: '#722ed1', borderColor: '#722ed1' }}
                    >
                      Bắt đầu tạo ảnh (1.5:1)
                    </Button>
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Quá trình có thể mất 15-30 giây. Tỉ lệ ảnh mặc định là 1.5:1 (3:2).
                      </Text>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </TabPane>
        </Tabs>
      </Modal>
    </div>
  );
};

export default LifetreeDetail; 